import React, { useState } from 'react';
import { Sidebar, Menu, MenuItem, SubMenu, menuClasses } from 'react-pro-sidebar';
import { union, difference, isEmpty, sortBy } from 'lodash';
import { FormLabel, FormControlLabel, Checkbox, TextField, Button, ButtonGroup, Radio } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { IconTrash } from '../ui-kit/icons';

import CrewTypeLabel from '../general/CrewTypeLabel';
import GroupName from '../general/GroupName';
import { menuItemStyles, sideBarTheme, theme } from '../ui-kit/helpers';
import SkillIcon from '../general/SkillIcon';
import AvailabilityCodeIcon from '../general/AvailabilityCodeIcon';

const ContractStatsButtons = ({ timeUnit, onChange }) => (
  <ButtonGroup variant="outlined" fullWidth>
    <Button variant={timeUnit === 'shift' ? 'contained' : 'outlined'} fullWidth onClick={() => onChange('shift')}>
      {I18n.t('reports.group_reports.standby_duty_hours.mode.shifts')}
    </Button>
    <Button variant={timeUnit === 'hour' ? 'contained' : 'outlined'} fullWidth onClick={() => onChange('hour')}>
      {I18n.t('reports.group_reports.standby_duty_hours.mode.hours')}
    </Button>
    <Button variant={timeUnit === null ? 'contained' : 'outlined'} onClick={() => onChange(null)} fullWidth>
      {I18n.t('hide')}
    </Button>
  </ButtonGroup>
);

const SideBarFilters = ({
  filterState,
  filterOptions,
  filterChangeHandler,
  setContractStatsTimeUnit,
  contractStatsTimeUnit,
  savedSearches,
  saveSearch,
  applySearch,
  deleteSearch,
  visibilityStatus,
  setVisibilityStatusForColumn
}) => {
  const [newSavedSearchName, setNewSavedSearchName] = useState('');
  // Stations can have access to many skills and stations
  // We provide an option to filter these by name
  const [filtersByName, setFiltersByName] = useState({
    skillsByName: '',
    memberOfByName: '',
    availabilityCodesByName: ''
  });

  const handleSelectChange = (event, multi, removeValues) => {
    const filterKey = event.target.name;
    const previousValue = filterState[filterKey];
    const id = event.target.id;

    let newValue = [];

    if (previousValue.includes(id)) {
      newValue = difference(previousValue, [id]);
    } else {
      newValue = multi ? union(previousValue, [id]) : [id];

      // If remove values are provided, we will remove them. This is for nested selects
      // where the selection of a inner value should superscede the outer value
      // For example: selecting a team should unselect the parent station, or selecting
      // a station should all teams
      if (removeValues) newValue = difference(newValue, removeValues);
    }

    const newFilterState = { ...filterState, [filterKey]: newValue };
    filterChangeHandler(newFilterState);
  };

  const handleSelectAll = (collection, filterKey) => {
    const newFilterState = { ...filterState, [filterKey]: collection };
    filterChangeHandler(newFilterState);
  };

  const handleSelectClear = filterKey => {
    const newFilterState = { ...filterState, [filterKey]: [] };
    filterChangeHandler(newFilterState);
  };

  const handleTextFieldChange = event => {
    const filterKey = event.target.name;
    const value = event.target.value;

    const newFilterState = { ...filterState, [filterKey]: value };

    // Store the new filter state in the component state
    filterChangeHandler(newFilterState);
  };

  const setFilterAvailableOnly = value => {
    const newFilterState = { ...filterState, availableOnly: value };
    filterChangeHandler(newFilterState);
  };

  const setFilterByName = (field, value) => {
    const newFilterByName = {
      ...filtersByName,
      [field]: value
    };

    setFiltersByName(newFilterByName);
  };

  const filterCollectionByName = (collection, value) => {
    if (value === '') return collection;

    return collection.filter(item => item.name.toLowerCase().includes(value.toLowerCase()));
  };

  // Apply the filter by name to the skills (if it has a value set)
  const skillOptions = sortBy(filterCollectionByName(filterOptions.skills, filtersByName.skillsByName), 'name');

  // Apply the filter by name to the list of groups (memberOf) if it has a value set
  // Always show selected first
  const groupOptions = sortBy(filterCollectionByName(filterOptions.groups, filtersByName.memberOfByName), 'name');

  const availabilityCodeOptions = sortBy(
    filterCollectionByName(
      filterOptions.availabilityCodes.filter(ac => ac.available_for_duty_request),
      filtersByName.availabilityCodesByName
    ),
    'name'
  );

  const HideMenuButton = ({ columnName, status, fullWidth }) => (
    <Button
      variant={status ? 'outlined' : 'contained'}
      onClick={() => setVisibilityStatusForColumn(columnName, !status)}
      fullWidth={fullWidth}
    >
      {status ? I18n.t('hide') : I18n.t('show')}
    </Button>
  );

  const MenuDivider = ({ label }) => (
    <p style={{ marginTop: '20px', marginLeft: '12px', fontWeight: 'bold' }}>{label}</p>
  );

  const getSubMenuLabel = (text, values) => {
    return `${text} ${isEmpty(values) ? '' : `(${values.length})`}`;
  };

  const showTeamsFor = station => {
    const checkedValues = new Set((filterState['memberOf'] || []).map(o => o.toString()));
    const checked = checkedValues.has(station.id.toString());

    if (checked) return true;

    return station.teams.some(team => checkedValues.has(team.id.toString()));
  };

  const contractTypeNameOptions = ['any', ...filterOptions.contractTypeNames];

  return (
    <ThemeProvider theme={theme}>
      <Sidebar
        backgroundColor={sideBarTheme.sidebar.backgroundColor}
        style={{ minHeight: '800px', maxHeight: '800px', minWidth: '350px' }}
      >
        <Menu className="filtersMenu" menuItemStyles={menuItemStyles}>
          <MenuDivider label={I18n.t('schedules.exchanges.duty_request.request_filters')} />
          <MenuItem className="filterAvailableOnly">
            <FormControlLabel
              control={
                <Checkbox
                  name={'filter_available_only'}
                  checked={filterState.availableOnly || false}
                  onChange={() => setFilterAvailableOnly(!filterState.availableOnly)}
                />
              }
              label={I18n.t('schedules.exchanges.duty_exchange.candidates.show_available_only')}
            />
          </MenuItem>
          <MenuItem>
            <TextField
              id="name"
              name="name"
              variant="standard"
              size="large"
              label={I18n.t('name')}
              onChange={handleTextFieldChange}
              fullWidth
            />
          </MenuItem>
          <SubMenu
            className="skillsByName"
            label={getSubMenuLabel(I18n.t('activerecord.models.skill'), filterState.skills)}
          >
            <MenuItem>
              <TextField
                id="skills_by_name"
                name="skills_by_name"
                variant="standard"
                size="large"
                placeholder={I18n.t('search')}
                onChange={e => setFilterByName('skillsByName', e.target.value)}
                fullWidth
              />
            </MenuItem>
            <MenuItem>
              <ButtonGroup fullWidth variant="outlined">
                <HideMenuButton columnName="skills" status={visibilityStatus.skills} />
              </ButtonGroup>
            </MenuItem>
            {skillOptions.map(skill => (
              <MenuItem key={skill.id} rootStyles={{ [`.${menuClasses.button}`]: { height: '35px' } }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name={'skills'}
                      id={skill.id.toString()}
                      checked={(filterState['skills'] || []).includes(skill.id.toString())}
                      onChange={e => handleSelectChange(e, false)}
                    />
                  }
                  label={
                    <>
                      <SkillIcon skill={skill} /> {skill.name}
                    </>
                  }
                />
              </MenuItem>
            ))}
          </SubMenu>
          <SubMenu
            className="availabilityCodesByName"
            label={getSubMenuLabel(
              I18n.t('activerecord.models.schedule/availability_code.other'),
              filterState.availability_codes
            )}
          >
            <MenuItem>
              <TextField
                id="availability_codes_by_name"
                name="availability_codes_by_name"
                variant="standard"
                size="large"
                placeholder={I18n.t('search')}
                onChange={e => setFilterByName('availabilityCodesByName', e.target.value)}
                fullWidth
              />
            </MenuItem>
            <MenuItem>
              <ButtonGroup fullWidth variant="outlined">
                <HideMenuButton columnName="availability_codes" status={visibilityStatus.availability_codes} />
              </ButtonGroup>
            </MenuItem>
            {availabilityCodeOptions.length === 0 && <p>{I18n.t('no_results_found')}</p>}
            {availabilityCodeOptions.map(ac => {
              const checked = (filterState['availability_codes'] || []).includes(ac.id.toString());
              return (
                <MenuItem key={ac.id} rootStyles={{ [`.${menuClasses.button}`]: { height: '35px' } }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={'availability_codes'}
                        id={ac.id.toString()}
                        checked={checked}
                        onChange={e => handleSelectChange(e, false)}
                      />
                    }
                    label={
                      <>
                        <AvailabilityCodeIcon code={ac} /> {ac.name}
                      </>
                    }
                  />
                </MenuItem>
              );
            })}
          </SubMenu>
          <SubMenu
            className="crewType"
            label={getSubMenuLabel(I18n.t('activerecord.attributes.group.crew_type'), filterState.crew_type)}
          >
            <MenuItem>
              <ButtonGroup fullWidth variant="outlined">
                <HideMenuButton columnName="crew_type" status={visibilityStatus.crew_type} />
              </ButtonGroup>
            </MenuItem>
            {filterOptions.crewTypes.map(ct => {
              const checked = (filterState['crew_type'] || []).includes(ct);
              return (
                <MenuItem key={ct} rootStyles={{ [`.${menuClasses.button}`]: { height: '35px' } }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={'crew_type'}
                        id={ct}
                        checked={checked}
                        onChange={e => handleSelectChange(e, true)}
                      />
                    }
                    label={
                      <CrewTypeLabel
                        crewType={ct}
                        displayText={`${I18n.t(`classy_enum.station_crew_type.${ct}`)}`}
                        showUnknown={true}
                      />
                    }
                  />
                </MenuItem>
              );
            })}
          </SubMenu>
          <SubMenu className="memberOf" label={getSubMenuLabel(I18n.t('member_of'), filterState.memberOf)}>
            <MenuItem>
              <TextField
                id="groups_by_name"
                name="groups_by_name"
                variant="standard"
                size="large"
                placeholder={I18n.t('search')}
                onChange={e => setFilterByName('memberOfByName', e.target.value)}
                fullWidth
              />
            </MenuItem>
            <MenuItem>
              <ButtonGroup variant="outlined" fullWidth>
                <Button
                  onClick={() =>
                    handleSelectAll(
                      groupOptions.map(g => g.id.toString()),
                      'memberOf'
                    )
                  }
                >
                  {I18n.t('all')}
                </Button>
                <Button variant="outlined" fullWidth onClick={() => handleSelectClear('memberOf')}>
                  {I18n.t('none')}
                </Button>
              </ButtonGroup>
            </MenuItem>
            <MenuItem>
              <HideMenuButton columnName="memberOf" status={visibilityStatus.memberOf} fullWidth={true} />
            </MenuItem>

            {groupOptions.map(group => {
              const checkedValues = new Set((filterState['memberOf'] || []).map(o => o.toString()));
              const checked = checkedValues.has(group.id.toString());

              return (
                <React.Fragment key={group.id}>
                  <MenuItem rootStyles={{ [`.${menuClasses.button}`]: { height: '35px' } }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name={'memberOf'}
                          id={group.id.toString()}
                          checked={checked}
                          onChange={e =>
                            handleSelectChange(
                              e,
                              true,
                              group.teams.map(t => t.id.toString())
                            )
                          }
                        />
                      }
                      label={<GroupName group={group} />}
                    />
                  </MenuItem>
                  {showTeamsFor(group) &&
                    group.teams.map(team => (
                      <MenuItem
                        key={team.id}
                        rootStyles={{
                          [`.${menuClasses.button}`]: { height: '35px', marginLeft: '10px' }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              name={'memberOf'}
                              id={team.id.toString()}
                              checked={checkedValues.has(team.id.toString())}
                              onChange={e => handleSelectChange(e, true, [group.id.toString()])}
                            />
                          }
                          label={team.name}
                        />
                      </MenuItem>
                    ))}
                </React.Fragment>
              );
            })}
          </SubMenu>
          <SubMenu
            className="candidateType"
            label={getSubMenuLabel(
              I18n.t('schedules.exchanges.duty_exchange.candidates.candidate_type'),
              filterState.candidate_type
            )}
          >
            <MenuItem>
              <ButtonGroup fullWidth variant="outlined">
                <HideMenuButton columnName="candidate_type" status={visibilityStatus.candidate_type} />
              </ButtonGroup>
            </MenuItem>
            {filterOptions.candidateTypes.map(ct => {
              const checked = filterState['candidate_type'].includes(ct);
              return (
                <MenuItem
                  key={ct}
                  rootStyles={{ [`.${menuClasses.button}`]: { height: '35px' } }}
                  title={I18n.t(`classy_enum.duty_transaction_candidate_type.${ct}`)}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={'candidate_type'}
                        id={ct}
                        checked={checked}
                        onChange={e => handleSelectChange(e, true)}
                      />
                    }
                    label={I18n.t(`classy_enum.duty_transaction_candidate_type.${ct}`)}
                  />
                </MenuItem>
              );
            })}
          </SubMenu>
          <SubMenu
            className="contractStatistics"
            label={I18n.t('schedules.exchanges.duty_exchange.candidates.contract_statistics')}
          >
            <MenuItem>
              <ContractStatsButtons timeUnit={contractStatsTimeUnit} onChange={setContractStatsTimeUnit} />
            </MenuItem>
            <MenuItem disabled>
              <FormLabel>{I18n.t('activerecord.models.reporting/contracts/contract_type')}</FormLabel>
            </MenuItem>
            {contractTypeNameOptions.map(contractTypeName => (
              <MenuItem key={contractTypeName} rootStyles={{ [`.${menuClasses.button}`]: { height: '35px' } }}>
                <FormControlLabel
                  key={contractTypeName}
                  control={
                    <Radio
                      name={'contractTypeName'}
                      value={contractTypeName}
                      checked={contractTypeName === filterState.contractTypeName}
                      onChange={() => filterChangeHandler({ ...filterState, contractTypeName })}
                    />
                  }
                  label={contractTypeName === 'any' ? I18n.t('any') : contractTypeName}
                  title={contractTypeName === 'any' ? I18n.t('any') : contractTypeName}
                />
              </MenuItem>
            ))}
          </SubMenu>
          <MenuDivider label={I18n.t('schedules.exchanges.duty_exchange.candidates.saved_searches')} />
          {Object.entries(savedSearches).map(([id, value]) => (
            <MenuItem
              key={id}
              prefix={<p onClick={() => applySearch(id, value)}>{id}</p>}
              suffix={<IconTrash onClick={() => deleteSearch(id)} />}
            />
          ))}
          <MenuItem>
            <TextField
              id="new_filter_name"
              name="new_filter_name"
              variant="standard"
              label={I18n.t('save')}
              size="large"
              onChange={e => setNewSavedSearchName(e.target.value)}
              fullWidth
            />
          </MenuItem>
          <MenuItem>
            <Button className="saveFilters" onClick={() => saveSearch(newSavedSearchName)} variant="outlined" fullWidth>
              {I18n.t('save')}
            </Button>
          </MenuItem>
        </Menu>
      </Sidebar>
    </ThemeProvider>
  );
};

export default SideBarFilters;
